/**
 * Deterministic key colours for the species ID map (see `docs/RENDERING_ARCHITECTURE.md` §Capture).
 *
 * The ID map is a second render of the frame where every plant is painted a flat colour, one per
 * species, so the enhancement prompt can carry a legend — "#ff2020 is Acer rubrum" — and the image
 * model never has to guess which silhouette is which plant.
 *
 * That only works if the colours survive the round trip through a PNG and the model's own vision
 * encoder. Two constraints follow:
 *
 * 1. Channel values sit on a coarse lattice ({@link CHANNEL_STEPS} levels), so mild resampling can
 *    never slide one species' colour onto another's.
 * 2. Near-black and near-white are reserved — black is the background/terrain, and washed-out
 *    highlights drift toward white.
 *
 * Colours are derived from a hash of the definition id, so a species keeps its key colour across
 * sessions and captures without any stored table.
 */

/** Distinct values allowed per channel. 5 levels ⇒ 125 lattice points, minus reserved corners. */
const CHANNEL_STEPS = 5;

/** Lattice values: 0x20, 0x50, 0x80, 0xb0, 0xe0 — evenly spread, away from both extremes. */
const CHANNEL_VALUES: readonly number[] = [0x20, 0x50, 0x80, 0xb0, 0xe0];

/** The ID map's background/terrain colour. No species may be assigned it. */
export const ID_MAP_BACKGROUND = '#000000';

export interface SpeciesKey {
  /** Flora definition id this colour stands for. */
  definitionId: string;
  /** Lowercase `#rrggbb` used in both the render and the prompt legend. */
  hex: string;
}

/** FNV-1a: small, stable, and dependency-free — the same colour every run, on every machine. */
function hash(text: string): number {
  let value = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    value ^= text.charCodeAt(i);
    // 32-bit FNV prime multiply via shifts (avoids float precision loss on `* 16777619`).
    value = (value + ((value << 1) + (value << 4) + (value << 7) + (value << 8) + (value << 24))) >>> 0;
  }
  return value >>> 0;
}

function toHex(r: number, g: number, b: number): string {
  const part = (n: number): string => n.toString(16).padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
}

/**
 * A lattice point is unusable when all three channels sit at the same extreme: `#202020` is too
 * close to the black background, `#e0e0e0` reads as a blown highlight.
 */
function isReserved(ri: number, gi: number, bi: number): boolean {
  const last = CHANNEL_STEPS - 1;
  const allLow = ri === 0 && gi === 0 && bi === 0;
  const allHigh = ri === last && gi === last && bi === last;
  return allLow || allHigh;
}

/** Every assignable lattice colour, in a fixed order. */
function latticeColors(): string[] {
  const colors: string[] = [];
  for (let ri = 0; ri < CHANNEL_STEPS; ri += 1) {
    for (let gi = 0; gi < CHANNEL_STEPS; gi += 1) {
      for (let bi = 0; bi < CHANNEL_STEPS; bi += 1) {
        if (isReserved(ri, gi, bi)) continue;
        colors.push(
          toHex(
            CHANNEL_VALUES[ri] as number,
            CHANNEL_VALUES[gi] as number,
            CHANNEL_VALUES[bi] as number,
          ),
        );
      }
    }
  }
  return colors;
}

/** Number of distinct species colours available. */
export const PALETTE_SIZE = latticeColors().length;

/**
 * Assign a unique key colour to each definition id.
 *
 * Each id hashes to a preferred slot; collisions probe forward to the next free one. Sorting the ids
 * first makes the result depend only on the *set* of species present, not on placement order, so the
 * same planting always produces the same legend.
 */
export function assignSpeciesColors(definitionIds: readonly string[]): Map<string, string> {
  const colors = latticeColors();
  const unique = [...new Set(definitionIds)].sort();
  const assigned = new Map<string, string>();
  const taken = new Set<number>();

  for (const id of unique) {
    let slot = hash(id) % colors.length;
    // Linear probe. Bounded by colors.length, so it always terminates.
    for (let probe = 0; probe < colors.length && taken.has(slot); probe += 1) {
      slot = (slot + 1) % colors.length;
    }
    if (taken.has(slot)) {
      throw new Error(
        `Species palette exhausted: ${unique.length} species exceeds ${colors.length} available key colours.`,
      );
    }
    taken.add(slot);
    assigned.set(id, colors[slot] as string);
  }

  return assigned;
}

/**
 * Key-colour id for a site object.
 *
 * Buildings share one palette with plants rather than getting their own, so the two can never be
 * assigned the same colour — a shed decoding as *Acer rubrum* would be a worse failure than either
 * being missing. Namespacing the id keeps them distinguishable while `assignSpeciesColors` handles
 * both in a single pass. Site objects are keyed individually (not by kind), because a manifest
 * describes *this* shed at *this* size, not sheds in general.
 */
export function siteObjectKeyId(siteObjectId: string): string {
  return `site:${siteObjectId}`;
}

/**
 * Key-colour id for a placed creature or figure.
 *
 * Keyed by **species**, not per instance — the opposite of a site object, and for the reason that
 * makes both right. A shed is one of a kind and the manifest describes that shed; a flock of
 * fourteen goldfinches is one fact repeated, and giving each bird its own colour would spend
 * fourteen legend lines and fourteen lattice points saying it. Plants are keyed by species for
 * exactly the same reason.
 *
 * Namespaced so a creature can never be assigned a plant's colour, or a building's.
 */
export function faunaKeyId(faunaDefinitionId: string): string {
  return `fauna:${faunaDefinitionId}`;
}

/** Parse a `#rrggbb` key colour into 0–255 channels, for the renderer's material swap. */
export function keyColorChannels(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace('#', '');
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
}
